import type { Card, GameAction, PublicGameState } from '@durakapp/engine';

export type IntentKind = GameAction['type'] | 'wait' | 'illegal';

export interface CardIntent {
  kind: IntentKind;
  action?: GameAction;
  reason?: string;
}

/**
 * Pure function: given my state-of-mind (the public state, my id, the card I tapped,
 * and optionally a target slot for defence) decide which engine GameAction the
 * client should emit. The server re-validates everything; this is just the UX.
 */
export function intentForCardTap(
  state: PublicGameState,
  myId: string,
  card: Card,
  targetSlotIndex?: number,
): CardIntent {
  if (state.phase === 'finished') return { kind: 'illegal', reason: 'match finished' };
  const isAttacker = state.attackerId === myId;
  const isDefender = state.defenderId === myId;
  const tableEmpty = state.table.length === 0;
  const hasUndefended = state.table.some((s) => s.defense === null);

  // I'm the defender and there's an undefended slot — try to defend.
  if (isDefender && hasUndefended) {
    const slot = targetSlotIndex !== undefined
      ? state.table[targetSlotIndex]
      : state.table.find((s) => s.defense === null);
    if (!slot) return { kind: 'wait', reason: 'pick a slot to defend' };
    const idx =
      targetSlotIndex !== undefined
        ? targetSlotIndex
        : state.table.findIndex((s) => s.defense === null);
    return {
      kind: 'defend',
      action: { type: 'defend', playerId: myId, card, targetSlotIndex: idx },
    };
  }

  // I'm the attacker and the table is empty — first attack.
  if (isAttacker && tableEmpty) {
    return { kind: 'attack', action: { type: 'attack', playerId: myId, card } };
  }

  // Anyone but the defender can throw in once the table has cards.
  if (!isDefender && !tableEmpty) {
    return { kind: 'throw_in', action: { type: 'throw_in', playerId: myId, card } };
  }

  // Defender, all defended (phase=='attack') — they don't act with a card.
  return { kind: 'wait', reason: 'not your turn to play this card' };
}

export type ButtonIntent = 'take' | 'pass' | null;

export function buttonIntent(state: PublicGameState, myId: string): {
  canTake: boolean;
  canPass: boolean;
} {
  if (state.phase === 'finished') return { canTake: false, canPass: false };
  const canTake =
    state.defenderId === myId &&
    state.phase === 'defense' &&
    state.table.length > 0;
  const canPass =
    state.attackerId === myId &&
    state.phase === 'attack' &&
    state.table.length > 0 &&
    state.table.every((s) => s.defense !== null);
  return { canTake, canPass };
}
